import React, { useEffect, useState } from 'react';

// SelfTestProbe gives every page a machine-readable test surface. Append
// ?test=true to any route and this fetches /api/selftest — the same deterministic
// contract the Go server and e2e tests use — exposes it on
// window.__APPNZ_SELFTEST__, and renders a hidden [data-testid="selftest"] node
// carrying data-ok plus the full JSON. Integration tests (and humans debugging)
// can then pull each page's health without scraping the rendered UI.
//
// It is SSR-safe and hydration-safe: it renders null on the server and on the
// first client render (data is still null), so the prerendered and hydrated
// trees match; the probe element only appears after the fetch resolves.
export const SelfTestProbe: React.FC = () => {
  const enabled =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('test') === 'true';
  const [data, setData] = useState<unknown>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    fetch('/api/selftest')
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        (window as unknown as { __APPNZ_SELFTEST__?: unknown }).__APPNZ_SELFTEST__ = d;
        setData(d);
      })
      .catch((e) => {
        if (!cancelled) setData({ ok: false, error: String(e) });
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  if (!enabled || data === null) return null;
  const ok = Boolean((data as { ok?: boolean }).ok);
  return (
    <div data-testid="selftest" data-ok={String(ok)} hidden>
      {JSON.stringify(data)}
    </div>
  );
};
